import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthState } from "./types";
import {
  clearRefreshToken,
  clearToken,
  decodePayload,
  isExpired,
  loadRefreshToken,
  loadToken,
  storeRefreshToken,
  storeToken,
  toUser,
} from "./token";
import { beginLogin, endSession, refreshAccessToken } from "./oauthClient";

interface AuthContextValue {
  state: AuthState;
  signIn: () => Promise<void>;
  signOut: () => void;
  // refresh re-reads the token from sessionStorage. OauthCallback calls
  // this after storing a freshly-exchanged token so the rest of the app
  // sees the authenticated state without a full page reload.
  refresh: () => void;
  authHeader: Record<string, string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function deriveState(): AuthState {
  const token = loadToken();
  if (!token) return { status: "unauthenticated" };
  const payload = decodePayload(token);
  if (!payload || isExpired(payload)) {
    clearToken();
    return { status: "unauthenticated" };
  }
  return { status: "authenticated", user: toUser(payload), token };
}

export function OidcProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => deriveState());
  const refreshInFlight = useRef(false);

  useEffect(() => {
    // Re-derive when sessionStorage changes from another tab.
    const handler = () => setState(deriveState());
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Rotate the 1h access token via the refresh_token grant. On any failure
  // the tokens are cleared — ProtectedRoute then restarts the interactive
  // flow (which Nova OS's session cookie usually completes silently).
  const doRefresh = useCallback(async () => {
    if (refreshInFlight.current) return;
    const rt = loadRefreshToken();
    if (!rt) return;
    refreshInFlight.current = true;
    try {
      const res = await refreshAccessToken(rt);
      if (res) {
        storeToken(res.token);
        storeRefreshToken(res.refreshToken);
      } else {
        clearToken();
        clearRefreshToken();
      }
      setState(deriveState());
    } finally {
      refreshInFlight.current = false;
    }
  }, []);

  // Proactive renewal: schedule a refresh ~2 min before the access token's
  // exp. A timer that oversleeps (laptop lid closed) fires late with an
  // already-expired access token — that's fine, the refresh token is what
  // the grant validates.
  useEffect(() => {
    if (state.status !== "authenticated") return;
    const payload = decodePayload(state.token);
    if (!payload?.exp) return;
    const fireIn = Math.max(payload.exp * 1000 - Date.now() - 120_000, 5_000);
    const id = window.setTimeout(() => void doRefresh(), fireIn);
    return () => window.clearTimeout(id);
  }, [state, doRefresh]);

  // Mount-time recovery: the access token expired while the tab was
  // closed/asleep (deriveState dropped it) but a refresh token survives —
  // renew instead of forcing the user through the redirect dance.
  useEffect(() => {
    if (state.status === "unauthenticated" && loadRefreshToken()) void doRefresh();
    // mount only — later unauthenticated transitions clear the refresh token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = useCallback(async () => {
    await beginLogin(window.location.pathname + window.location.search);
  }, []);

  const signOut = useCallback(() => {
    // Order matters: navigate to Nova OS end-session BEFORE flipping
    // local state. If we setState({unauthenticated}) first, React
    // re-renders, ProtectedRoute kicks in, and signIn() navigates to
    // /oauth/authorize — beating endSession() and skipping cookie
    // invalidation, so Nova OS silently re-auths the user (regression
    // surfaced in prod 2026-06-01).
    clearToken();
    clearRefreshToken();
    endSession();
  }, []);

  const refresh = useCallback(() => {
    setState(deriveState());
  }, []);

  const authHeader = useMemo<Record<string, string>>(() => {
    const h: Record<string, string> = {};
    if (state.status === "authenticated") h.Authorization = `Bearer ${state.token}`;
    return h;
  }, [state]);

  const value: AuthContextValue = { state, signIn, signOut, refresh, authHeader };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth consumed outside <OidcProvider>");
  return ctx;
}
